import React, {
	type ComponentPropsWithoutRef,
	useEffect,
	useRef,
	useState,
	memo,
	useCallback,
} from 'react';
import { styled } from '@compiled/react';
import throttle from 'lodash/throttle';
import type { PreloadedQuery } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { WidthObserver } from '@atlaskit/width-detector';
import {
	AsyncAdminInaccessibleBoard,
	useShouldShowAdminInaccessibleBoard,
} from '@atlassian/jira-admin-inaccessible-board';
import { ViewTracker } from '@atlassian/jira-analytics-web-react';
import { BulkOpsAnalyticsProvider } from '@atlassian/jira-bulk-operations-analytics';
import { useReferenceLoader } from '@atlassian/jira-bulk-operations-common';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout';
import { getLongTasksMetrics } from '@atlassian/jira-common-long-task-metrics';
import { filterReporter } from '@atlassian/jira-common-long-task-metrics/src/reporters/software-filters';
import { contentHorizontalSpacing } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { ISSUE_HIERARCHY_LEVEL_BASE } from '@atlassian/jira-issue-type-hierarchies';
import { IccProvider } from '@atlassian/jira-platform-inline-card-create';
import {
	ContextualAnalyticsData,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { aggBacklogDataQuery$data } from '@atlassian/jira-relay/src/__generated__/aggBacklogDataQuery.graphql';
import type { viewBacklogCustomFiltersQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogCustomFiltersQuery.graphql';
import type { viewBacklogVersionFilterRelayQuery } from '@atlassian/jira-relay/src/__generated__/viewBacklogVersionFilterRelayQuery.graphql';
import { uifBacklogResource } from '@atlassian/jira-router-resources-uif-backlog';
import { NextGenBacklogSkeleton } from '@atlassian/jira-skeletons';
import type { PrefetchedDataGql } from '@atlassian/jira-software-backlog-load-agility-graphql-data/src/types';
import IssueLimitFlags from '@atlassian/jira-software-issue-limit-flags';
import { ProjectKeyboardShortcuts } from '@atlassian/jira-software-keyboard-shortcuts/async';
import { useEvent } from '@atlassian/jira-software-react-use-event';
import { StorageLimitsBanner } from '@atlassian/jira-storage-limits-banner';
import { UpFlowPersistentUpgradeBanner } from '@atlassian/jira-up-flow-persistent-upgrade-banner/src/async';
import { AddBoardToHistory } from '@atlassian/jira-view-history';
import { ANALYTICS_SOURCE_BACKLOG } from '../common/constants/analytics';
import { useStoreFields, type FieldConfigs } from '../common/hooks/use-store-fields';
import { isBacklogWithRelayEnabled } from '../feature-flags';
import { CAN_EDIT_BOARD_CONFIG } from '../model/config/permissions/constants';
import type { JiraPlanDataRestResponse } from '../rest/fetch-plan-data/types';
import { useBacklogSelector } from '../state';
import type { EpicIssueTypeState } from '../state/reducers/config/epic/types';
import { isScrumBacklogStrategy } from '../state/selectors/app';
import { getCardsCount, getOrderedCardIdsByCardListId } from '../state/selectors/cards';
import { getPermissions } from '../state/selectors/permissions';
import { useIsCMPBacklog } from '../state/state-hooks/capabilities';
import AllFieldsSync from './all-fields-sync';
import BacklogContent from './backlog-content';
import { CmpBacklogSlaTracker } from './backlog-sla-tracker';
import { BulkOperationsLoader } from './bulk-operations-loader';
import { BulkOperationsSidebar } from './bulk-operations-sidebar';
import CardList from './card-list';
import DetailView from './detail-view';
import DocumentTitle from './document-title';
import DragDropNoSelect from './drag-drop-no-select';
import { FiltersStorage } from './filters-storage';
import GlobalSearchTracker from './global-search-tracker';
import Header from './header';
import KeyboardShortcuts from './keyboard-shortcuts';
import Modals from './modals';
import BoardIssueMoveDialog from './modals/board-issue-move-dialog';
import PrefetchIssues from './prefetch-issues';
import Realtime from './realtime';
import SubmitApdex from './submit-apdex';
import SyncWithRouting from './sync-with-routing';

export type Props = {
	isCacheHit: boolean;
	isDetailViewVisible: boolean;
	issueHierarchyLevel?: number;
	cardListIds: string[];
	boardId: number;
	baseUrl: string;
	projectKey: string;
	customFiltersQueryRef: PreloadedQuery<viewBacklogCustomFiltersQuery> | null | undefined;
	versionFilterRelayQueryRef: PreloadedQuery<viewBacklogVersionFilterRelayQuery> | null | undefined;
	onBacklogView: (analyticsEvent: UIAnalyticsEvent) => void;
	onEpicPanelClose: () => void;
	onVersionPanelClose: () => void;
};

export type PropsWithFields = Props & {
	isCacheHit: boolean;
	prefetchedData: PrefetchedDataGql | null;
	prefetchedAggData: aggBacklogDataQuery$data | null;
	prefetchedPlanData: JiraPlanDataRestResponse | null;
	storyPointFieldId: string | null;
	epicIssueType: EpicIssueTypeState | null;
};

const BacklogComponent = ({
	isCacheHit,
	boardId,
	baseUrl,
	projectKey,
	isDetailViewVisible,
	issueHierarchyLevel = ISSUE_HIERARCHY_LEVEL_BASE,
	cardListIds,
	onBacklogView: onBacklogViewProp,
	onEpicPanelClose: onEpicPanelCloseProp,
	onVersionPanelClose: onVersionPanelCloseProp,
	customFiltersQueryRef,
	versionFilterRelayQueryRef,
}: Props) => {
	const isViewEventSent = useRef(false);
	const [width, setWidth] = useState<number | undefined>(0);
	const [openSidebar, setOpenSidebar] = useState(false);
	const throttledSetWidth = throttle(setWidth, 50);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const shouldShowAdminInaccessibleBoard = useShouldShowAdminInaccessibleBoard(uifBacklogResource);

	const onBacklogView = useEvent(onBacklogViewProp);
	const onEpicPanelClose = useEvent(onEpicPanelCloseProp);
	const onVersionPanelClose = useEvent(onVersionPanelCloseProp);

	const onOpenSidebar = useCallback(() => {
		setOpenSidebar(true);
	}, []);

	const onCloseSidebar = useCallback(() => {
		setOpenSidebar(false);
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			getLongTasksMetrics('filter').stop(filterReporter);
		}, 1);

		document.body?.classList.add('spb');

		return () => {
			document.body?.classList.remove('spb');
			clearTimeout(timer);
		};
	}, []);

	useEffect(() => {
		if (!isViewEventSent.current && cardListIds.length > 0) {
			isViewEventSent.current = true;

			const analyticsEvent = createAnalyticsEvent({});
			onBacklogView(analyticsEvent);
		}
	}, [onBacklogView, cardListIds.length, createAnalyticsEvent]);
	const isCMPBoard = useIsCMPBacklog();

	const bulkOperationsToolbar = useReferenceLoader();

	const bulkOperationsForm = useReferenceLoader();

	const shouldShowIssueLimitFlags =
		(isCMPBoard && ff('jsw-board-backlog-show-5k-issues-with-flag')) ||
		(!isCMPBoard && ff('jsw-board-backlog-show-5k-issues-with-flag-tmp'));

	const issueCount = useBacklogSelector(getCardsCount);
	const userCanEditBoardConfig = useBacklogSelector(getPermissions)[CAN_EDIT_BOARD_CONFIG];

	const backlogContainerIssueCount = useBacklogSelector(getOrderedCardIdsByCardListId).BACKLOG
		?.length;
	const isScrumBacklog = useBacklogSelector(isScrumBacklogStrategy);

	const getIssueLimitFlagViews = () => {
		if (isCMPBoard) {
			return [{ view: 'BACKLOG' as const, issueCount }];
		}

		return [
			{
				view: 'BACKLOG' as const,
				issueCount: backlogContainerIssueCount,
			},
			{
				view: isScrumBacklog ? ('SPRINTS' as const) : ('BOARD' as const),
				issueCount: issueCount - backlogContainerIssueCount,
			},
		];
	};

	// created to avoid duplication and will be moved back when cleaning up the FF.
	const backlogContainerContent = (
		<Container data-test-id="software-backlog.backlog" data-testid="software-backlog.backlog">
			<DocumentTitle />
			<StorageLimitsBanner />
			<UpFlowPersistentUpgradeBanner isBannerEnabled />
			<PageHeaderWrapper
				data-test-id="software-backlog.page-header"
				data-testid="software-backlog.page-header"
			>
				<Header
					customFiltersQueryRef={customFiltersQueryRef}
					versionFilterRelayQueryRef={versionFilterRelayQueryRef}
				/>
			</PageHeaderWrapper>
			<ContextualAnalyticsData
				attributes={{
					analyticsSource: ANALYTICS_SOURCE_BACKLOG,
				}}
			>
				<SizeDetectorWrapper>
					<Realtime>
						<IccProvider>
							<WidthObserver setWidth={throttledSetWidth} />
							<BacklogContent
								isDetailViewVisible={isDetailViewVisible}
								parentWidth={width || 0}
								boardId={boardId}
								cardListIds={cardListIds}
								DetailView={DetailView}
								SubmitApdex={SubmitApdex}
								isCacheHit={isCacheHit}
								CardList={CardList}
								BoardIssueMoveDialog={BoardIssueMoveDialog}
								onEpicPanelClose={onEpicPanelClose}
								onVersionPanelClose={onVersionPanelClose}
								issueHierarchyLevel={issueHierarchyLevel}
								bulkOperationsToolbar={bulkOperationsToolbar}
							/>
						</IccProvider>
					</Realtime>
				</SizeDetectorWrapper>
			</ContextualAnalyticsData>
			<PrefetchIssues />
			<Modals />
			<DragDropNoSelect />
			<ViewTracker />
			<AddBoardToHistory baseUrl={baseUrl} boardId={String(boardId)} />
			<SyncWithRouting />
			<KeyboardShortcuts />
			<AllFieldsSync />
			<FiltersStorage />
			{shouldShowIssueLimitFlags && (
				<IssueLimitFlags
					boardId={boardId}
					isCMP={isCMPBoard}
					isProjectAdmin={userCanEditBoardConfig}
					projectKey={projectKey}
					views={getIssueLimitFlagViews()}
				/>
			)}
		</Container>
	);

	// Show a message to admin users if the board is inaccessible due to filter permissions and how to update it
	if (
		ff('show-board-filter-permission-message-to-admins_iqh25') &&
		shouldShowAdminInaccessibleBoard
	) {
		return (
			<AsyncAdminInaccessibleBoard
				rapidViewId={boardId.toString()}
				skeleton={NextGenBacklogSkeleton}
			/>
		);
	}

	return (
		<>
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="backlog">
				{isCMPBoard && <CmpBacklogSlaTracker />}
				<GlobalSearchTracker />
				<BulkOpsAnalyticsProvider>
					<Wrapper>
						<MainContent>{backlogContainerContent}</MainContent>
						<BulkOperationsSidebar
							parentWidth={width || 0}
							isOpen={openSidebar}
							bulkOperationsForm={bulkOperationsForm}
						/>
					</Wrapper>
					<BulkOperationsLoader
						bulkOperationsForm={bulkOperationsForm}
						bulkOperationsToolbar={bulkOperationsToolbar}
						onCloseSidebar={onCloseSidebar}
						onOpenSidebar={onOpenSidebar}
					/>
				</BulkOpsAnalyticsProvider>
				{!ff('sea-2744-jsw-tabs-nav') && (
					<ProjectKeyboardShortcuts
						boardId={boardId}
						projectKey={projectKey}
						isCMPBoard={isCMPBoard}
					/>
				)}
			</ContextualAnalyticsData>
		</>
	);
};

export const Backlog =
	memo<
		JSX.LibraryManagedAttributes<
			typeof BacklogComponent,
			ComponentPropsWithoutRef<typeof BacklogComponent>
		>
	>(BacklogComponent);

// TODO: This is a flaky design. We shouldn't depend on prefetched data being available at certain points.
// As of current if prefetched data is null (which is a valid state), then the issue fields won't be initialized correctly.
// While there is sync. for lots of values, there is not sync for certain required values, such as `HIERARCHY...` fields. On
// that case, switching into the issue fields epic renderers will hide epics.
// In particular, src/packages/issue/fields/parent-switcher/src/controllers/with-sweet-state-parent-switcher/index.tsx
// won't render the field.
// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	prefetchedData,
	prefetchedAggData,
	prefetchedPlanData,
	storyPointFieldId,
	epicIssueType,
	...rest
}: PropsWithFields) => {
	const {
		storeFieldsFromPrefetchedData,
		storeFieldsFromPrefetchedAggData,
		storeFieldsFromPrefetchedPlanData,
	} = useStoreFields();

	// Initialise extracted fields (Sweet State) using prefetched data (SSR)
	useEffect(() => {
		const fieldConfigs: FieldConfigs = { storyPointFieldId };

		if (prefetchedPlanData) {
			storeFieldsFromPrefetchedPlanData(prefetchedPlanData, epicIssueType);
		} else if (isBacklogWithRelayEnabled() && prefetchedAggData) {
			storeFieldsFromPrefetchedAggData(prefetchedAggData, fieldConfigs);
		} else if (prefetchedData) {
			storeFieldsFromPrefetchedData(prefetchedData, fieldConfigs);
		}
	}, [
		prefetchedData,
		prefetchedAggData,
		prefetchedPlanData,
		storeFieldsFromPrefetchedData,
		storeFieldsFromPrefetchedAggData,
		storeFieldsFromPrefetchedPlanData,
		storyPointFieldId,
		epicIssueType,
	]);

	return <Backlog {...rest} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP})`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP})`,
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	outline: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MainContent = styled.div({
	flex: '1 0 0',
	display: 'flex',
	overflow: 'hidden',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SizeDetectorWrapper = styled.div({
	maxHeight: '100%',
	overflow: 'hidden',
	display: 'flex',
	flex: 1,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		height: 'auto !important',
		maxWidth: '100%',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PageHeaderWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	padding: `0 ${contentHorizontalSpacing}px`,
});
